import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { DEEP_CLEAN_ITEMS, statusColor } from './collaborationTypes';
import { EvidenceUpload, PhotoGallery, ReportPhotos } from './EvidencePhotos';
import './collaboration.css';
import { DeepCleanHistoryPanel } from './DeepCleanHistoryPanel';
import { ReportActivityList } from './ReportActivityList';
import {TaskWorkItems} from './TaskWorkItems';
import {summarizeReports} from '../propertyissue/propertyIssueSummary';

const issueLabels = {MAINTENANCE: 'Property Require Maintenance', DEEP_CLEAN: 'Property Require Deep Clean', OTHER: 'Property Issue'};
function IssueDate({value}) {
    const date = value ? new Date(value) : null;
    if (!date || Number.isNaN(date.getTime())) return <>Not recorded</>;
    return <time dateTime={date.toISOString()}>{date.toLocaleString('en-AU', {
        day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
    })}</time>;
}

export function ReportTasksPanel({ api, role, propertyId, cosJobId, onIssueSummaryChanged }) {
    const [reports, setReports] = useState([]), [tasks, setTasks] = useState([]);
    const [loading, setLoading] = useState(true), [error, setError] = useState(''), [revision, setRevision] = useState(0);
    const [tasksLoading, setTasksLoading] = useState(true), [tasksError, setTasksError] = useState('');
    const [selected, setSelected] = useState([]), [form, setForm] = useState(), [editingReport, setEditingReport] = useState();
    const [deepHistory, setDeepHistory] = useState(false);
    const formContainer = useRef(null);
    const pendingRead = useRef(null), active = useRef(true);
    const taskReportRead = useRef(null);
    useEffect(() => {
        if (!form) return;
        formContainer.current?.scrollIntoView?.({block: 'start'});
        formContainer.current?.focus({preventScroll: true});
    }, [form, editingReport?.id]);
    const isAdmin = role === 'ADMIN', canCreate = ['ADMIN', 'WORKER', 'SUPERVISOR'].includes(role);
    const canManage = ['ADMIN', 'AGENT', 'PROPERTY_MANAGER'].includes(role);
    const load = useCallback((keepSummaryOnClose = false) => {
        pendingRead.current?.controller.abort();
        taskReportRead.current?.abort();
        const abort = new AbortController();
        pendingRead.current = {controller: abort, keepSummaryOnClose};
        if (active.current) {
            setLoading(true);
            setError('');
            setTasksLoading(true);
            setTasksError('');
        }
        void api(`/properties/${propertyId}/reports`, 'GET', undefined, abort.signal).then(reports => {
            if (!abort.signal.aborted) {
                if (active.current) setReports(reports);
                onIssueSummaryChanged?.(propertyId, summarizeReports(reports));
            }
        }).catch(e => { if (!abort.signal.aborted) {
            if (active.current) {
                setError(e.message);
                setReports([]);
            }
            onIssueSummaryChanged?.(propertyId, undefined);
        } })
            .finally(() => { if (!abort.signal.aborted && active.current)
            setLoading(false); });
        void api(`/properties/${propertyId}/tasks`, 'GET', undefined, abort.signal).then(tasks => {
            if (!abort.signal.aborted && active.current) setTasks(tasks);
        }).catch(e => { if (!abort.signal.aborted && active.current) {
            setTasksError(e.message);
        } }).finally(() => { if (!abort.signal.aborted && active.current) setTasksLoading(false); });
    }, [api, propertyId, onIssueSummaryChanged]);
    const refresh = useCallback(() => {
        onIssueSummaryChanged?.(propertyId, undefined);
        if (active.current) setRevision(value => value + 1);
        // Saving may finish after the dialog closes. Complete the summary read
        // for the outer cards even when the panel no longer needs its own data.
        load(true);
    }, [load, onIssueSummaryChanged, propertyId]);
    useEffect(() => {
        active.current = true;
        load();
        return () => {
            active.current = false;
            if (!pendingRead.current?.keepSummaryOnClose) pendingRead.current?.controller.abort();
        };
    }, [load]);
    const dispatched = useMemo(() => new Set(tasks.filter(task => task.status !== 'Cancelled').flatMap(task => task.items.map(item => item.report_item_id))), [tasks]);
    const sortedReports = useMemo(() => [...reports].sort((left, right) =>
        (Date.parse(right.reported_at || '') || 0) - (Date.parse(left.reported_at || '') || 0)), [reports]);
    async function taskChanged(nextTask) {
        // A read started before this mutation must not restore stale Task/Report data.
        pendingRead.current?.controller.abort();
        if (active.current) {
            setTasks(current => current.map(task => task.job_id === nextTask.job_id ? nextTask : task));
            setRevision(value => value + 1);
            setLoading(false);
            setTasksLoading(false);
        }
        taskReportRead.current?.abort();
        const request = new AbortController();
        taskReportRead.current = request;
        onIssueSummaryChanged?.(propertyId, undefined);
        // Refresh the Report indicators without unmounting other items' drafts.
        // Finish the outer-card summary even if this panel closes during the read.
        try {
            const nextReports = await api(`/properties/${propertyId}/reports`, 'GET', undefined, request.signal);
            if (!request.signal.aborted) {
                if (active.current) {setReports(nextReports); setError('');}
                onIssueSummaryChanged?.(propertyId, summarizeReports(nextReports));
            }
        } catch (e) {
            if (!request.signal.aborted) {
                if (active.current) setError(e.message);
                onIssueSummaryChanged?.(propertyId, undefined);
            }
        }
    }
    const toggle = (id) => setSelected(current => current.includes(id) ? current.filter(value => value !== id) : [...current, id]);
    return <section className="collaboration-work" aria-label="Property Issues">
        <div className="work-actions">
            {canCreate ? <button type="button" disabled={Boolean(form)} onClick={() => {setEditingReport(undefined); setForm('report');}}>New Issue</button> : null}
            <button type="button" disabled={loading || tasksLoading || Boolean(form)} onClick={refresh}>Refresh</button>
            {isAdmin && selected.length > 0 ? <button type="button" disabled={loading || tasksLoading || Boolean(error) || Boolean(tasksError) || Boolean(form)} onClick={() => setForm('task')}>
                Arrange Task ({selected.length} selected)
            </button> : null}
        </div>
        {error ? <p role="alert">{error}</p> : null}
        {loading ? <p role="status">Loading issues…</p> : null}
        {form ? <div ref={formContainer} tabIndex={-1}>
            <WorkForm key={`${form}:${editingReport?.id || ''}`} kind={form} api={api} propertyId={propertyId} cosJobId={cosJobId}
                report={form === 'report' ? editingReport : undefined} selected={selected}
                existingTasks={tasks.filter(task => !task.items.length && !['Cancelled', 'ReadyForBilling'].includes(task.status))}
                onClose={() => setForm(undefined)} onSaved={() => {setForm(undefined); setSelected([]); refresh();}}/>
        </div> : null}
        {!loading && !error && !reports.length ? <p>There are no issues for this property. All good!</p> : null}
        {!loading && !error ? sortedReports.map(report => <ReportCard key={report.id} report={report} api={api}
            canManage={canManage} canSelect={isAdmin} canArrange={isAdmin && !tasksLoading && !tasksError}
            selected={selected} dispatched={dispatched} onToggle={toggle} onChanged={refresh}
            onDefine={() => {setEditingReport(report); setForm('report');}}/>) : null}
        <details className="issue-tools">
            <summary>Tasks ({tasks.length})</summary>
            {tasksError ? <p role="alert">{tasksError}</p> : null}
            {tasksLoading ? <p role="status">Loading tasks…</p> : null}
            {isAdmin ? <div className="work-actions"><button type="button" disabled={loading || tasksLoading || Boolean(error) || Boolean(tasksError) || Boolean(form)} onClick={() => setForm('task')}>
                {selected.length ? `Arrange Task (${selected.length} selected)` : 'Preventive / general Task'}
            </button></div> : null}
            {!tasksLoading && !tasksError && !tasks.length ? <p>No Task records.</p> : null}
            {tasks.map(task => <TaskHistoryRow key={task.job_id} task={task} api={api} canEdit={canCreate && !tasksLoading && !tasksError}
                canManage={isAdmin && !tasksLoading && !tasksError} onChanged={taskChanged}/>)}
        </details>
        <details className="issue-tools" onToggle={event => setDeepHistory(event.currentTarget.open)}>
            <summary>Deep clean needs, history and exports</summary>
            {deepHistory ? <DeepCleanHistoryPanel key={`${propertyId}:${revision}`} api={api} propertyId={propertyId}/> : null}
        </details>
    </section>;
}
function ReportCard({ report, api, canManage, canSelect, canArrange, selected, dispatched, onToggle, onChanged, onDefine }) {
    const [reason, setReason] = useState(report.blocked_reason || ''), [error, setError] = useState(''), [busy, setBusy] = useState(false);
    async function update(status) {
        if (busy)
            return;
        setBusy(true);
        setError('');
        try {
            await api(`/reports/${report.id}`, 'PUT', { status, reason });
            onChanged();
        }
        catch (e) {
            setError(e instanceof Error ? e.message : 'Unable to update Report.');
        }
        finally {
            setBusy(false);
        }
    }
    const items = report.items.filter(item => item.cycle === report.cycle);
    const previousItems = report.items.filter(item => item.cycle !== report.cycle);
    const hasResolution = report.status === 'CLOSED' || report.resolved_at || report.resolve_description;
    return <article className="work-card" style={{borderLeftColor: statusColor(report.status)}}>
        <div className="issue-heading">
            <h3>{report.legacy_category === 'key' ? 'Property Key Missing' : issueLabels[report.category] || report.category.replaceAll('_', ' ')}</h3>
            <span className="issue-status" style={{color: statusColor(report.status)}}>{report.status.toLowerCase()}</span>
        </div>
        <p className="issue-description"><strong>Description: </strong>{report.description}</p>
        <p><strong>Reported Date: </strong><IssueDate value={report.reported_at}/></p>
        {report.blocked_reason ? <p><strong>Blocked: </strong>{report.blocked_reason}</p> : null}
        <ReportPhotos api={api} reportId={report.id} imageIds={report.image_ids} resolveImageIds={report.resolve_image_ids}>
            {hasResolution ? <section className="issue-resolution" aria-label="Resolution">
                <h4>Resolution</h4>
                <p><strong>Date: </strong><IssueDate value={report.resolved_at}/></p>
                <p className="issue-description"><strong>Description: </strong>{report.resolve_description || 'No description'}</p>
                <p><strong>Resolved By: </strong>{report.resolved_by_name || report.resolved_by || 'Not recorded'}</p>
            </section> : null}
        </ReportPhotos>
        {items.length ? <details className="issue-tools">
            <summary>Work items ({items.filter(item => item.status === 'DONE').length}/{items.length} complete)</summary>
            {items.map(item => <div className="work-item" key={item.id}>
                <label>{canArrange && report.status !== 'CLOSED' && item.status !== 'DONE' && !dispatched.has(item.id)
                    ? <input type="checkbox" checked={selected.includes(item.id)} onChange={() => onToggle(item.id)}/> : null}{item.description}</label>
                <span style={{color: statusColor(item.status)}}>{item.status}{dispatched.has(item.id) && item.status !== 'DONE' ? ' · Task arranged' : ''}</span>
                {item.completed_at ? <span>Completed <IssueDate value={item.completed_at}/> · Worker {item.completed_by}</span> : null}
            </div>)}
        </details> : null}
        {canManage ? <details className="issue-tools">
            <summary>Follow up</summary>
            <fieldset disabled={busy} aria-label="Follow up">
                <label>Reason<textarea value={reason} onChange={event => setReason(event.target.value)} maxLength={4000}/></label>
                <div className="work-actions">
                    {report.status !== 'OPEN' ? <button type="button" onClick={() => void update('OPEN')}>
                        {report.status === 'CLOSED' ? 'Reopen Report' : 'Mark open'}
                    </button> : null}
                    {report.status !== 'CLOSED' ? <>
                        <button type="button" disabled={!reason.trim()} onClick={() => void update('BLOCKED')}>Blocked</button>
                        {!items.some(item => item.status !== 'DONE') ? <button type="button" onClick={() => void update('CLOSED')}>Resolve</button> : null}
                        {!items.length && canSelect ? <button type="button" onClick={onDefine}>Define work items</button> : null}
                    </> : null}
                </div>
            </fieldset>
        </details> : null}
        {error ? <p role="alert">{error}</p> : null}
        {report.activity.length || previousItems.length ? <details className="issue-tools">
            <summary>Issue history</summary>
            <p>Cycle {report.cycle}</p>
            {previousItems.map(item => <p key={item.id}>Cycle {item.cycle} · {item.description} · {item.status} · {item.completed_at ? <IssueDate value={item.completed_at}/> : 'No completion recorded'}</p>)}
            <ReportActivityList key={`${report.id}:${report.activity[0]?.id}`} api={api} report={report}/>
        </details> : null}
    </article>;
}
function TaskHistoryRow(props) {
    const [open, setOpen] = useState(false);
    const { task } = props;
    return _jsxs("details", { className: "work-card", onToggle: event => setOpen(event.currentTarget.open), children: [_jsxs("summary", { children: [task.cos_job_id, " \u00B7 ", task.subtype === 'DEEP_CLEAN' ? 'Deep clean' : 'General Task', " \u00B7 ", task.status, " \u00B7 ", `${task.items.filter(item => item.status === 'DONE').length}/${task.items.length} complete`, " \u00B7 Assigned: ", task.worker || 'Unassigned'] }), open ? _jsx(TaskWorkItems, { ...props }) : null] });
}
function WorkForm({ kind, api, propertyId, cosJobId, report, selected, existingTasks, onClose, onSaved }) {
    const [category, setCategory] = useState(report?.category || 'DEEP_CLEAN'), [description, setDescription] = useState(report?.description || '');
    const [codes, setCodes] = useState([]), [custom, setCustom] = useState(''), [date, setDate] = useState('');
    const [worker, setWorker] = useState(''), [workers, setWorkers] = useState([]);
    const [existingId, setExistingId] = useState(''), [busy, setBusy] = useState(false), [error, setError] = useState('');
    const [photos, setPhotos] = useState([]), [uploading, setUploading] = useState(false);
    const request = useRef(undefined), submitting = useRef(false);
    useEffect(() => {
        if (kind !== 'task')
            return;
        const abort = new AbortController();
        void api('/task-workers', 'GET', undefined, abort.signal).then(setWorkers).catch(e => { if (!abort.signal.aborted)
            setError(e.message); });
        return () => abort.abort();
    }, [api, kind]);
    async function submit() {
        if (submitting.current)
            return;
        const items = codes.map(code => ({ item_code: code, description: DEEP_CLEAN_ITEMS.find(item => item[0] === code)?.[1] || code }));
        if (custom.trim())
            items.push({ item_code: category === 'DEEP_CLEAN' ? 'CUSTOM' : 'GENERAL', description: custom.trim() });
        if (!description.trim() || (!items.length && (kind === 'report' || !selected.length))) {
            setError('Add a description and at least one work item.');
            return;
        }
        submitting.current = true;
        setBusy(true);
        setError('');
        try {
            if (kind === 'report') {
                if (report)
                    await api(`/reports/${report.id}`, 'PUT', { status: 'OPEN', reason: description, items });
                else {
                    const payload = { property_id: propertyId, cos_job_id: cosJobId, category, description, items, image_ids: photos.map(photo => photo.id) };
                    const fingerprint = JSON.stringify(payload);
                    if (request.current?.fingerprint !== fingerprint)
                        request.current = { key: crypto.randomUUID(), fingerprint };
                    await api('/reports', 'POST', { ...payload, request_key: request.current.key });
                }
            }
            else {
                const payload = { subtype: category === 'DEEP_CLEAN' ? 'DEEP_CLEAN' : 'GENERAL', report_item_ids: selected, items,
                    job: { id: existingId ? Number(existingId) : undefined, property_id: propertyId, description, date: date || undefined, cos_job_worker: worker || undefined } };
                const fingerprint = JSON.stringify(payload);
                if (request.current?.fingerprint !== fingerprint)
                    request.current = { key: crypto.randomUUID(), fingerprint };
                await api('/tasks', 'POST', { ...payload, request_key: request.current.key });
            }
            onSaved();
        }
        catch (e) {
            setError(e instanceof Error ? e.message : 'Unable to save. Your draft is retained.');
        }
        finally {
            submitting.current = false;
            setBusy(false);
        }
    }
    return _jsx("form", { className: "work-card", onSubmit: event => { event.preventDefault(); void submit(); }, children: _jsxs("fieldset", { disabled: busy, children: [_jsx("legend", { children: kind === 'report' ? report ? 'Define Report work items' : 'New Report' : 'Arrange Task' }), error ? _jsx("p", { role: "alert", children: error }) : null, _jsxs("label", { children: ["Category", _jsxs("select", { value: category, onChange: event => { setCategory(event.target.value); setCodes([]); }, children: [_jsx("option", { value: "DEEP_CLEAN", children: "Deep clean" }), kind === 'report' ? _jsxs(_Fragment, { children: [_jsx("option", { value: "MAINTENANCE", children: "Maintenance" }), _jsx("option", { value: "OTHER", children: "Other" })] }) : _jsx("option", { value: "GENERAL", children: "General Task" })] })] }), _jsxs("label", { children: ["Description", _jsx("textarea", { required: true, value: description, onChange: event => setDescription(event.target.value), maxLength: 4000 })] }), selected.length && kind === 'task' ? _jsxs("p", { children: [selected.length, " Report items selected."] }) : null, category === 'DEEP_CLEAN' ? _jsx("div", { className: "work-catalog", children: DEEP_CLEAN_ITEMS.map(([code, label]) => _jsxs("label", { children: [_jsx("input", { type: "checkbox", checked: codes.includes(code), onChange: () => setCodes(current => current.includes(code) ? current.filter(item => item !== code) : [...current, code]) }), label] }, code)) }) : null, _jsxs("label", { children: ["Additional work item (e.g. Bathroom 3)", _jsx("input", { value: custom, onChange: event => setCustom(event.target.value), maxLength: 4000 })] }), kind === 'task' ? _jsxs(_Fragment, { children: [_jsxs("label", { children: ["Scheduled date (optional)", _jsx("input", { type: "date", value: date, onChange: event => setDate(event.target.value) })] }), _jsxs("label", { children: ["Assigned worker", _jsxs("select", { value: worker, onChange: event => setWorker(event.target.value), children: [_jsx("option", { value: "", children: "Unassigned / default scheduler" }), workers.map(worker => _jsx("option", { value: worker.id, children: worker.name }, worker.id))] })] }), existingTasks.length ? _jsxs("label", { children: ["Existing Task", _jsxs("select", { value: existingId, onChange: event => setExistingId(event.target.value), children: [_jsx("option", { value: "", children: "Create a new Task" }), existingTasks.map(task => _jsx("option", { value: task.job_id, children: task.cos_job_id }, task.job_id))] })] }) : null] }) : null, kind === 'report' && !report ? _jsxs(_Fragment, { children: [_jsx(EvidenceUpload, { api: api, path: `/properties/${propertyId}/report-photos`, count: photos.length, onPhoto: photo => setPhotos(current => [...current, photo]), onBusy: setUploading }), _jsx(PhotoGallery, { photos: photos })] }) : null, _jsxs("div", { className: "work-actions", children: [_jsx("button", { type: "submit", disabled: uploading, children: busy ? 'Saving…' : 'Save' }), _jsx("button", { type: "button", disabled: uploading, onClick: onClose, children: "Cancel" })] })] }) });
}
